import React from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import Login from '../pages/Login'
import ProtectedRoute from '../components/ProtectedRoute'
import AdminLayout from '../components/admin/AdminLayout'
import ProtectedFeatureRoute from '../components/admin/ProtectedFeatureRoute'
import AutoPlaceholder from '../features/_shared/AutoPlaceholder'
import PlayerDetailLayout from '../features/players/pages/PlayerDetailLayout'
import TournamentDetailLayout from '../features/tournaments/pages/TournamentDetailLayout'

const DashboardHomePage = React.lazy(() => import('../features/dashboard/pages/DashboardHomePage'))
const PlayersListPage = React.lazy(() => import('../features/players/pages/PlayersListPage'))
const AddUser = React.lazy(() => import('../containers/User/Add'))
const UserEdit = React.lazy(() => import('../containers/User/Edit'))
const AddLevel = React.lazy(() => import('../containers/User/Level/Add'))

const TournamentList = React.lazy(() => import('../containers/Tournament/List'))
const TournamentAdd = React.lazy(() => import('../containers/Tournament/Add'))
const TournamentEdit = React.lazy(() => import('../containers/Tournament/Edit'))

const TableActiveList = React.lazy(() => import('../containers/Table/ActiveList'))
const TableAdd = React.lazy(() => import('../containers/Table/Add'))
const TableEdit = React.lazy(() => import('../containers/Table/Edit'))

const DepositsPage = React.lazy(() => import('../features/wallet/pages/DepositsPage'))
const WithdrawalsPage = React.lazy(() => import('../features/wallet/pages/WithdrawalsPage'))
const AdjustmentsPage = React.lazy(() => import('../features/wallet/pages/AdjustmentsPage'))
const AdminResourcePage = React.lazy(() => import('../features/adminResources/AdminResourcePage'))
const BonusTemplatesPage = React.lazy(() => import('../features/promotions/BonusTemplatesPage'))
const SupportTicketsPage = React.lazy(() => import('../features/support/SupportTicketsPage'))
const ReportsPage = React.lazy(() => import('../features/reports/ReportsPage'))
const StaffPage = React.lazy(() => import('../features/settings/StaffPage'))
const RolesPage = React.lazy(() => import('../features/settings/RolesPage'))
const TableDetailPage = React.lazy(() => import('../features/tables/TableDetailPage'))
const PlayerDetailTabPage = React.lazy(() => import('../features/players/pages/PlayerDetailTabPage'))
const TournamentDetailTabPage = React.lazy(() => import('../features/tournaments/pages/TournamentDetailTabPage'))

function RedirectUserEdit() {
  const { id } = useParams()
  return <Navigate to={`/players/${id}/edit`} replace />
}

function RedirectTableEdit() {
  const { id } = useParams()
  return <Navigate to={`/tables/${id}/edit`} replace />
}

function RedirectTournamentEdit() {
  const { id } = useParams()
  return <Navigate to={`/tournaments/${id}/edit`} replace />
}

export default function AppRoutes() {
  const withPermission = (permission, element, title) => (
    <ProtectedFeatureRoute needs={permission} title={title}>
      {element}
    </ProtectedFeatureRoute>
  )

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/forgot-password" element={<AutoPlaceholder />} />
      <Route path="/reset-password/:token?" element={<AutoPlaceholder />} />
      <Route path="/security" element={<AutoPlaceholder />} />

      <Route path="/user/list" element={<Navigate to="/players" replace />} />
      <Route path="/user/add" element={<Navigate to="/players/new" replace />} />
      <Route path="/user/edit/:id" element={<RedirectUserEdit />} />

      <Route path="/table/active" element={<Navigate to="/tables" replace />} />
      <Route path="/table/add" element={<Navigate to="/tables/new" replace />} />
      <Route path="/table/edit/:id" element={<RedirectTableEdit />} />

      <Route path="/tournament/list" element={<Navigate to="/tournaments" replace />} />
      <Route path="/tournament/add" element={<Navigate to="/tournaments/new" replace />} />
      <Route path="/tournament/edit/:id" element={<RedirectTournamentEdit />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardHomePage />} />

        <Route path="players" element={<PlayersListPage />} />
        <Route path="players/new" element={<AddUser />} />
        <Route path="players/:playerId/edit" element={<UserEdit />} />
        <Route path="players/:playerId" element={<PlayerDetailLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<PlayerDetailTabPage />} />
          <Route path="balances" element={<PlayerDetailTabPage />} />
          <Route path="transactions" element={<PlayerDetailTabPage />} />
          <Route path="gameplay" element={<PlayerDetailTabPage />} />
          <Route path="security" element={<PlayerDetailTabPage />} />
          <Route path="notes-tags" element={<PlayerDetailTabPage />} />
          <Route path="kyc" element={<PlayerDetailTabPage />} />
          <Route path="compliance" element={<PlayerDetailTabPage />} />
        </Route>

        <Route path="user/level/add" element={<AddLevel />} />

        <Route path="wallet/deposits" element={<DepositsPage />} />
        <Route path="wallet/withdrawals" element={<WithdrawalsPage />} />
        <Route path="wallet/transactions/:transactionId" element={<AdminResourcePage />} />
        <Route
          path="wallet/adjustments"
          element={withPermission('wallet.adjustments.manage', <AdjustmentsPage />, 'Adjustment access restricted')}
        />
        <Route path="wallet/payment-methods" element={<AdminResourcePage />} />

        <Route path="tournaments" element={<TournamentList />} />
        <Route path="tournaments/new" element={<TournamentAdd />} />
        <Route path="tournaments/:id/edit" element={<TournamentEdit />} />
        <Route path="tournaments/:id" element={<TournamentDetailLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<TournamentDetailTabPage />} />
          <Route path="registrations" element={<TournamentDetailTabPage />} />
          <Route path="blinds" element={<TournamentDetailTabPage />} />
          <Route path="prizes" element={<TournamentDetailTabPage />} />
          <Route path="schedule" element={<TournamentDetailTabPage />} />
        </Route>

        <Route path="tables" element={<TableActiveList />} />
        <Route path="tables/new" element={<TableAdd />} />
        <Route path="tables/:id/edit" element={<TableEdit />} />
        <Route path="tables/:id" element={<TableDetailPage />} />

        <Route path="sit-and-go" element={<AdminResourcePage />} />
        <Route path="sit-and-go/new" element={<AdminResourcePage />} />
        <Route path="sit-and-go/:sngId" element={<AdminResourcePage />} />

        <Route path="fraud/alerts" element={<AdminResourcePage />} />
        <Route path="fraud/cases/:caseId" element={<AdminResourcePage />} />
        <Route path="fraud/reviews/shared-endpoints" element={<AdminResourcePage />} />
        <Route path="fraud/reviews/collusion" element={<AdminResourcePage />} />
        <Route path="fraud/reviews/chip-dumping" element={<AdminResourcePage />} />
        <Route
          path="fraud/sanctions"
          element={withPermission('fraud.sanctions.manage', <AdminResourcePage />, 'Sanctions access restricted')}
        />

        <Route path="kyc/queue" element={<AdminResourcePage />} />
        <Route path="kyc/reviews/:reviewId" element={<AdminResourcePage />} />
        <Route path="kyc/logs" element={<AdminResourcePage />} />
        <Route path="kyc/restrictions" element={<AdminResourcePage />} />

        <Route path="promotions/bonuses" element={<BonusTemplatesPage />} />
        <Route path="promotions/bonuses/new" element={<BonusTemplatesPage />} />
        <Route path="promotions/bonuses/:bonusId" element={<BonusTemplatesPage />} />
        <Route path="promotions/codes" element={<AdminResourcePage />} />
        <Route path="promotions/campaigns/:campaignId" element={<AdminResourcePage />} />
        <Route path="promotions/rakeback" element={<AdminResourcePage />} />

        <Route path="vip/tiers" element={<AdminResourcePage />} />
        <Route path="vip/tiers/:tierId" element={<AdminResourcePage />} />
        <Route path="vip/rules" element={<AdminResourcePage />} />

        <Route path="leaderboards" element={<AdminResourcePage />} />
        <Route path="leaderboards/:leaderboardId" element={<AdminResourcePage />} />
        <Route path="missions" element={<AdminResourcePage />} />
        <Route path="missions/:missionId" element={<AdminResourcePage />} />
        <Route path="achievements" element={<AdminResourcePage />} />
        <Route path="achievements/:achievementId" element={<AdminResourcePage />} />

        <Route path="support/tickets" element={<SupportTicketsPage />} />
        <Route path="support/tickets/:ticketId" element={<SupportTicketsPage />} />
        <Route path="support/templates" element={<AdminResourcePage />} />

        <Route path="cms/banners" element={<AdminResourcePage />} />
        <Route path="cms/promo-blocks" element={<AdminResourcePage />} />
        <Route path="cms/faq" element={<AdminResourcePage />} />
        <Route path="cms/notifications" element={<AdminResourcePage />} />
        <Route path="cms/announcements" element={<AdminResourcePage />} />
        <Route path="cms/popups" element={<AdminResourcePage />} />

        <Route path="reports" element={<ReportsPage />} />

        <Route path="settings/staff" element={<StaffPage />} />
        <Route
          path="settings/roles"
          element={withPermission('settings.roles.manage', <RolesPage />, 'Role management restricted')}
        />
        <Route path="settings/platform" element={<AdminResourcePage />} />
        <Route path="settings/feature-flags" element={<AdminResourcePage />} />
        <Route
          path="settings/security"
          element={withPermission('settings.security.manage', <AdminResourcePage />, 'Security settings restricted')}
        />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
